import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import loginImage from "../static/media/Login/login.webp";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();

  const initialEmail = useMemo(() => {
    return location.state?.email || "";
  }, [location.state]);

  const [form, setForm] = useState({
    email: initialEmail,
    code: "",
    new_password: "",
    confirm_password: "",
  });

  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState({
    type: location.state?.message ? "success" : "",
    message: location.state?.message || "",
  });
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
      general: "",
    }));

    if (banner.message) {
      setBanner({ type: "", message: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const email = form.email.trim().toLowerCase();
    const code = form.code.trim();

    if (!email) {
      newErrors.email = "Ingresa tu correo electrónico";
    } else if (!emailRegex.test(email)) {
      newErrors.email = "Ingresa un correo válido";
    }

    if (!code) {
      newErrors.code = "Ingresa el código recibido";
    } else if (!/^\d{5}$/.test(code)) {
      newErrors.code = "El código debe tener 5 dígitos";
    }

    if (!form.new_password) {
      newErrors.new_password = "Ingresa tu nueva contraseña";
    } else if (form.new_password.length < 6) {
      newErrors.new_password = "La contraseña debe tener al menos 6 caracteres";
    }

    if (!form.confirm_password) {
      newErrors.confirm_password = "Confirma tu nueva contraseña";
    } else if (form.new_password !== form.confirm_password) {
      newErrors.confirm_password = "Las contraseñas no coinciden";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setBanner({ type: "", message: "" });

    try {
      const response = await fetch("/api/gym/reset-password/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: form.email.trim().toLowerCase(),
          code: form.code.trim(),
          new_password: form.new_password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const message = data.error || "No se pudo actualizar la contraseña";

        setErrors((prev) => ({
          ...prev,
          general: message,
        }));

        setBanner({
          type: "error",
          message,
        });
        return;
      }

      navigate("/login", {
        state: {
          email: form.email.trim().toLowerCase(),
          message: "Contraseña actualizada correctamente. Ya puedes iniciar sesión.",
        },
      });
    } catch {
      setBanner({
        type: "error",
        message: "Ocurrió un error al conectar con el servidor",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    const email = form.email.trim().toLowerCase();

    if (!email) {
      setErrors((prev) => ({
        ...prev,
        email: "Ingresa tu correo para reenviar el código",
      }));
      return;
    }

    if (!emailRegex.test(email)) {
      setErrors((prev) => ({
        ...prev,
        email: "Ingresa un correo válido",
      }));
      return;
    }

    setResendLoading(true);
    setBanner({ type: "", message: "" });

    try {
      const response = await fetch("/api/gym/forgot-password/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        setBanner({
          type: "error",
          message: data.error || "No se pudo reenviar el código",
        });
        return;
      }

      setBanner({
        type: "success",
        message: "Te enviamos un nuevo código de recuperación",
      });
    } catch {
      setBanner({
        type: "error",
        message: "Ocurrió un error al conectar con el servidor",
      });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#ffffff",
        py: { xs: 5, md: 7 },
      }}
    >
      <Container
        maxWidth={false}
        sx={{ width: { xs: "90%", md: "82%" }, maxWidth: "1380px", mx: "auto" }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            gridTemplateRows: { xs: "auto auto", lg: "auto 1fr" },
            gap: { xs: 4, md: 4.5, lg: 5 },
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              gridColumn: { xs: "1", lg: "1 / span 2" },
              textAlign: "center",
              maxWidth: 860,
              mx: "auto",
              mb: { xs: 0.5, md: 1.2 },
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                px: 1.5,
                py: 0.7,
                borderRadius: 999,
                bgcolor: "rgba(220,38,38,0.08)",
                border: "1px solid rgba(220,38,38,0.12)",
                mb: 1.8,
              }}
            >
              <Typography
                sx={{
                  color: "#dc2626",
                  fontWeight: 800,
                  fontSize: { xs: 12.8, md: 13.2 },
                  letterSpacing: "0.02em",
                }}
              >
                RECUPERAR ACCESO
              </Typography>
            </Box>

            <Typography
              component="h1"
              sx={{
                color: "#111111",
                fontWeight: 800,
                lineHeight: 1.02,
                letterSpacing: -1.2,
                fontSize: {
                  xs: "clamp(34px, 8vw, 44px)",
                  md: "clamp(42px, 3.4vw, 58px)",
                },
                mb: 1.5,
              }}
            >
              Restablece tu contraseña NovaXFit
            </Typography>

            <Typography
              sx={{
                color: "rgba(17,17,17,0.72)",
                lineHeight: 1.8,
                fontSize: { xs: 15.5, md: 16.5 },
                fontWeight: 500,
                maxWidth: 760,
                mx: "auto",
              }}
            >
              Ingresa el código enviado a tu correo y define una nueva contraseña
              para recuperar el acceso a tu cuenta de{" "}
              <Box component="strong" sx={{ fontWeight: 800, color: "#111111" }}>
                NovaXFit
              </Box>
              .
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1.2, sm: 1.4 }}
              sx={{
                mt: 2.4,
                justifyContent: "center",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  px: 1.4,
                  py: 1,
                  borderRadius: "14px",
                  bgcolor: "#ffffff",
                  border: "1px solid rgba(17,17,17,0.08)",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                }}
              >
                <MarkEmailReadRoundedIcon sx={{ color: "#dc2626", fontSize: 20 }} />
                <Typography sx={{ fontSize: 14.2, fontWeight: 800, color: "#111111" }}>
                  Código por correo
                </Typography>
              </Stack>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  px: 1.4,
                  py: 1,
                  borderRadius: "14px",
                  bgcolor: "#ffffff",
                  border: "1px solid rgba(17,17,17,0.08)",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                }}
              >
                <SecurityRoundedIcon sx={{ color: "#dc2626", fontSize: 20 }} />
                <Typography sx={{ fontSize: 14.2, fontWeight: 800, color: "#111111" }}>
                  Proceso seguro
                </Typography>
              </Stack>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  px: 1.4,
                  py: 1,
                  borderRadius: "14px",
                  bgcolor: "#ffffff",
                  border: "1px solid rgba(17,17,17,0.08)",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                }}
              >
                <LockResetRoundedIcon sx={{ color: "#dc2626", fontSize: 20 }} />
                <Typography sx={{ fontSize: 14.2, fontWeight: 800, color: "#111111" }}>
                  Nueva contraseña
                </Typography>
              </Stack>
            </Stack>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", lg: "flex-end" },
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: { xs: 220, md: 340, lg: 400 },
                mx: "auto",
                borderRadius: { xs: "26px", md: "30px" },
                overflow: "hidden",
                background:
                  "linear-gradient(135deg, #090909 0%, #161616 34%, #2a0a0d 68%, #0d0d0d 100%)",
                border: "1px solid rgba(17,17,17,0.08)",
                boxShadow: "0 28px 70px rgba(0,0,0,0.12)",
                minHeight: { xs: 300, md: 330, lg: 380 },
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "radial-gradient(circle at top right, rgba(220,38,38,0.22) 0%, transparent 32%), radial-gradient(circle at bottom left, rgba(255,255,255,0.06) 0%, transparent 26%)",
                  pointerEvents: "none",
                }}
              />
              <motion.img
                src={loginImage}
                alt="Acceso seguro NovaXFit"
                initial={{ opacity: 0, y: 28, scale: 0.97 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7 }}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "center", lg: "flex-start" },
              width: "100%",
              minHeight: { xs: "auto", md: 350, lg: 400 },
            }}
          >
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                width: "100%",
                maxWidth: 460,
                borderRadius: { xs: "24px", md: "28px" },
                border: "1px solid rgba(17,17,17,0.08)",
                bgcolor: "#ffffff",
                boxShadow: "0 24px 70px rgba(0,0,0,0.08)",
                p: { xs: 2.2, sm: 3, md: 3.2 },
              }}
            >
              <Stack spacing={2}>
                {banner.message && (
                  <Alert
                    severity={banner.type}
                    sx={{
                      borderRadius: "14px",
                      "& .MuiAlert-message": {
                        fontWeight: 600,
                      },
                    }}
                  >
                    {banner.message}
                  </Alert>
                )}

                <TextField
                  fullWidth
                  label="Correo electrónico"
                  type="email"
                  value={form.email}
                  onChange={handleChange("email")}
                  error={Boolean(errors.email)}
                  helperText={errors.email || " "}
                  variant="outlined"
                  autoComplete="email"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "16px",
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Código de recuperación"
                  value={form.code}
                  onChange={handleChange("code")}
                  error={Boolean(errors.code)}
                  helperText={errors.code || " "}
                  variant="outlined"
                  inputProps={{ maxLength: 5 }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "16px",
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Nueva contraseña"
                  type={showPassword ? "text" : "password"}
                  value={form.new_password}
                  onChange={handleChange("new_password")}
                  error={Boolean(errors.new_password)}
                  helperText={errors.new_password || " "}
                  variant="outlined"
                  autoComplete="new-password"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "16px",
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => setShowPassword((prev) => !prev)}
                          sx={{ color: "#111111" }}
                          aria-label={
                            showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                          }
                        >
                          {showPassword ? (
                            <VisibilityOffRoundedIcon />
                          ) : (
                            <VisibilityRoundedIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Confirmar nueva contraseña"
                  type={showConfirmPassword ? "text" : "password"}
                  value={form.confirm_password}
                  onChange={handleChange("confirm_password")}
                  error={Boolean(errors.confirm_password)}
                  helperText={errors.confirm_password || " "}
                  variant="outlined"
                  autoComplete="new-password"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "16px",
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                          sx={{ color: "#111111" }}
                          aria-label={
                            showConfirmPassword
                              ? "Ocultar confirmación de contraseña"
                              : "Mostrar confirmación de contraseña"
                          }
                        >
                          {showConfirmPassword ? (
                            <VisibilityOffRoundedIcon />
                          ) : (
                            <VisibilityRoundedIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                    mt: -0.5,
                  }}
                >
                  <Link
                    component={RouterLink}
                    to="/login"
                    underline="none"
                    sx={{
                      color: "rgba(17,17,17,0.72)",
                      fontWeight: 700,
                      fontSize: 14,
                      "&:hover": {
                        color: "#111111",
                      },
                    }}
                  >
                    Volver al inicio de sesión
                  </Link>

                  <Link
                    component="button"
                    type="button"
                    underline="none"
                    onClick={handleResendCode}
                    disabled={resendLoading}
                    sx={{
                      color: "#dc2626",
                      fontWeight: 700,
                      fontSize: 14,
                      cursor: resendLoading ? "default" : "pointer",
                      opacity: resendLoading ? 0.7 : 1,
                      "&:hover": {
                        color: "#b91c1c",
                      },
                    }}
                  >
                    {resendLoading ? "Reenviando..." : "Reenviar código"}
                  </Link>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  sx={{
                    mt: 0.5,
                    textTransform: "none",
                    fontWeight: 800,
                    borderRadius: "16px",
                    py: 1.25,
                    fontSize: "1rem",
                    color: "#ffffff",
                    background:
                      "linear-gradient(135deg, #5f0008 0%, #a80d19 35%, #df2632 62%, #161616 100%)",
                    boxShadow: "0 18px 50px rgba(220,38,38,0.22)",
                    border: "1px solid rgba(0,0,0,0.08)",
                    "&:hover": {
                      filter: "brightness(1.05)",
                      boxShadow: "0 22px 56px rgba(220,38,38,0.26)",
                    },
                    "&.Mui-disabled": {
                      color: "#ffffff",
                      opacity: 0.75,
                    },
                  }}
                >
                  {loading ? "Actualizando..." : "Restablecer contraseña"}
                </Button>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}